import type { FilterExpression } from '@/types/query';

/**
 * Cross-filter merge for drill-down chips: when the user clicks two
 * different bars in the same chart, the two `eq` drill-down filters
 * collapse into a single `in` filter so the dashboard returns the
 * union of the two values rather than `nothing` (eq + eq with
 * different values would AND together to an empty set).
 *
 * `existing` is the previously-applied drilldown filter on the same
 * column (may be `eq` or `in`); `next` is the freshly-clicked `eq`
 * filter. Returns the merged `in` filter, or `null` when the new
 * value is already present (caller should leave state untouched).
 *
 * Pure helper — extracted from `dataset-store.addFilter` (R-8) so
 * the merge logic is testable without touching Zustand.
 */
export function mergeDrilldown(
  existing: FilterExpression,
  next: FilterExpression,
): FilterExpression | null {
  const existingValues =
    existing.operator === 'in'
      ? (existing.value as Array<string | number>)
      : [existing.value as string | number];
  const newVal = next.value as string | number;
  if (existingValues.includes(newVal)) return null;
  return {
    column: next.column,
    operator: 'in',
    value: [...existingValues, newVal],
    source: 'drilldown',
  };
}
